import React, { useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { secondsToTime } from '../../utilities/scoringUtilities';

import Card from 'react-bootstrap/Card';
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { ScoringInputType } from '../../models/enums/ScoringInputType';

const SupremacyGraph = (props) => {
    const {labels, datapoints, dataIds, getHighlighted} = props;

    const [dataForTooltip, setDataForTooltip] = useState(null);

    const externalTooltipHandler = (context) => {

        const {chart, tooltip} = context;

        if (tooltip.opacity === 0) {
            setDataForTooltip(null);
            return;
        }

        setDataForTooltip(context.tooltip)
    }

    ChartJS.register(
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        Title,
        Tooltip,
        Legend,
        Filler
    );

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 0
        },
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                enabled: false,
                position: 'nearest',
                external: externalTooltipHandler
            },
        },
        scales: {
            y: {
                grid: {
                    drawBorder: false,
                    color: function (context) {
                        if (context.tick.value > 0) {
                            return 'rgb(75, 192, 192)';
                        } else if (context.tick.value < 0) {
                            return 'rgb(255, 99, 132)';
                        }

                        return '#000000';
                    },
                }
            },
            x: {
                display: true,
                position: 'bottom',
                ticks: {
                    stepSize: 5,
                    autoSkip: false,
                    callback: function (val, index) {
                        // Hide every 2nd tick label
                        return index % 2 === 0 ? (labels[val] == null ? secondsToTime(labels[val - 1]) : secondsToTime(labels[val])) : '';
                    },
                    min: 0,
                    max: labels[labels.length - 1],
                    maxRotation: 0
                },
                scaleLabel: {
                    display: true,
                    labelString: 'Seconds',
                    fontSize: 16
                },
                title: {
                    display: true,
                    labelString: 'Seconds',
                    fontSize: 16
                }
            },
        }
    };

    const data = {
        labels,
        datasets: [
            {
                stepped: true,
                data: datapoints,
                borderColor: function (context) {
                    const chart = context.chart;
                    const {ctx, chartArea} = chart;

                    if (!chartArea) {
                        // This case happens on initial chart load
                        return;
                    }

                    const chartWidth = chartArea.right - chartArea.left;
                    const chartHeight = chartArea.bottom - chartArea.top;

                    const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
                    gradient.addColorStop(0, 'rgba(80, 80, 255, 0.5)');
                    gradient.addColorStop(1, 'rgba(252, 100, 100, 0.5)');


                    return gradient;
                },
                borderWidth: 2,
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
                pointStyle: 'circle',
                pointRadius: 8,
                pointHoverRadius: 12,
                spanGaps: false,
                fill: {
                    target: 'origin',
                    above: 'rgba(80, 80, 255, 0.5)',
                    below: 'rgba(252, 100, 100, 0.5)'
                }
            }
        ],
    };

    const getTooltip = (dataForTooltip, getHighlighted) => {
        let contents = <></>;
        if (!dataForTooltip) {
            contents = <span>Hover on a data point to get more information</span>
        } else {
            let ids = [];
            for (let i = 0; i < dataForTooltip.dataPoints.length; i++) {
                ids.push(dataIds[dataForTooltip.dataPoints[i].dataIndex]);
            }

            let matchesOnId = getHighlighted(ids);

            let timePartOfTooltip = Number.parseFloat(dataForTooltip.title[0]);

            let matches = matchesOnId.filter(x => x.time == timePartOfTooltip);

            if (matches && matches.length > 0) {
                let match = matches[0];

                let typeRow = <></>;

                if (match.type == ScoringInputType.ClipStart || match.type == ScoringInputType.ClipEnd) {
                    typeRow = <Row className="mt-1">
                        <Col xs="2">
                            <div>
                                <strong>Clip Start</strong>
                            </div>
                            <div>
                                {secondsToTime(match.clipStartsAt)}
                            </div>
                        </Col>
                        <Col xs="2">
                            <div>
                                <strong>Clip End</strong>
                            </div>
                            <div>
                                {secondsToTime(match.clipEndsAt)}
                            </div>
                        </Col>
                        <Col xs="2">
                            <div>
                                <strong>Score in clip</strong>
                            </div>
                            <div>
                                {match.scoreInClip}
                            </div>
                        </Col>
                        <Col xs="4">
                            <div>
                                <strong>Details</strong>
                            </div>
                            <div>
                                {match.type == ScoringInputType.ClipStart &&
                                    <span>{match.clipStartText}</span>
                                }

                                {match.type == ScoringInputType.ClipEnd &&
                                    <span>{match.clipEndText}</span>
                                }
                            </div>
                        </Col>
                        <Col xs="2">
                            <div>
                                <strong>Score at time</strong>
                            </div>
                            <div>
                                {match.cumulativeHomePoints} - {match.cumulativeAwayPoints}
                            </div>
                        </Col>
                    </Row>;
                } else if (match.type == ScoringInputType.PlayerScore) {
                    let scoreClass = (match.score >= 0 ? "text-success" : "text-danger");

                    typeRow = <Row className="mt-1">
                        <Col xs="2">
                            <div>
                                <strong>Player Position</strong>
                            </div>
                            <div>
                                {match.playerShirtNumber}
                            </div>
                        </Col>
                        <Col xs="2">
                            <div>
                                <strong>Raw Score</strong>
                            </div>
                            <div>
                                <span className={scoreClass}>{match.scoreText}</span>
                            </div>
                        </Col>
                        <Col xs="6">
                            <div>
                                <strong>Categorisation</strong>
                            </div>
                            <div>
                                {match.categorizationsText}
                            </div>
                        </Col>
                        <Col xs="2">
                            <div>
                                <strong>Score at time</strong>
                            </div>
                            <div>
                                {match.cumulativeHomePoints} - {match.cumulativeAwayPoints}
                            </div>
                        </Col>
                    </Row>;
                }

                contents = (<>
                    <Row>
                        <Col>
                            <div>
                                <strong>Time</strong>
                            </div>
                            <div>
                                {secondsToTime(match.time)}
                            </div>
                        </Col>
                        <Col>
                            <div>
                                <strong>Clip #</strong>
                            </div>
                            <div>
                                {match.clipNumber}
                            </div>
                        </Col>
                        <Col>
                            <div>
                                <strong>Half</strong>
                            </div>
                            <div>
                                {match.half}
                            </div>
                        </Col>
                        <Col>
                            <div>
                                <strong>Type</strong>
                            </div>
                            <div>
                                {match.typeText}
                            </div>
                        </Col>
                        <Col>
                            <div>
                                <strong>Supremacy</strong>
                            </div>
                            <div>
                                {
                                    match.type == 3 ? (match.clipStartSupremacy ? (Math.round(match.clipStartSupremacy * 1000) / 1000) : '') :
                                        (match.type == 4 ? (match.clipEndSupremacy ? (Math.round(match.clipEndSupremacy * 1000) / 1000) : '') :
                                            (match.type == 5 ? (match.actionSupremacy ? (Math.round(match.actionSupremacy * 1000) / 1000) : '') :
                                                ''))
                                }
                            </div>
                        </Col>
                        <Col>
                            <div>
                                <strong>Team</strong>
                            </div>
                            <div>
                                {match.teamName}
                            </div>
                        </Col>
                    </Row>

                    {typeRow}
                </>);
            }
        }


        return <Card className="p-2 m-2">{contents}</Card>
    }


    let tooltip = getTooltip(dataForTooltip, getHighlighted);


    return <>
        <div style={{whiteSpace: 'nowrap', maxHeight: 'calc(100vh - 325px)'}}>
            <div style={{height: '120px'}}>
                {tooltip}
            </div>
            <Line options={options} data={data}/>
        </div>
    </>

}

export default SupremacyGraph;